import { Link, notFound, useParams } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { adminQuoteQuery } from '@/queries';
import { ArrowLeft } from '@phosphor-icons/react';

import {
  addProofAction,
  convertQuoteAction,
  declineQuoteAction,
  priceQuoteAction,
} from '@/lib/admin-actions';
import { GarmentMockup } from '@/components/garment-mockup';
import { QuoteStatusBadge, formatDate } from '@/components/status-timeline';
import { Badge, Button, Callout, Field, inputClass, textareaClass } from '@/components/ui';
import { formatCents } from '@/lib/money';
import { locationLabel } from '@/lib/pricing/engine';

/**
 * Quote detail. AC-3.2, AC-3.3, AC-3.4.
 *
 * The three actions are staged so only the one that applies at the current
 * status is offered: price it, send a proof, convert it.
 *
 * The conversion panel used to show a projected commission. That belongs to the
 * platform's own agreement and not to this contract, which has none (FR-512),
 * and the table it was computed against does not exist here. T024f, R-165.
 */
export default function AdminQuoteDetail() {
  const { ref } = useParams({ strict: false }) as { ref: string };
  const { quote, product } = useSuspenseQuery(adminQuoteQuery(ref)).data;
  if (!quote) throw notFound();
  const canPrice = quote.status === 'submitted';
  const canProof = quote.status === 'priced' || quote.status === 'proof_sent';
  const canConvert = quote.status === 'approved';

  return (
    <div>
      <Link
        to="/admin/quotes"
        className="inline-flex items-center gap-2 py-2 text-sm font-semibold text-muted hover:text-ink"
      >
        <ArrowLeft size={16} weight="bold" aria-hidden />
        Quote queue
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-[26px] font-extrabold tracking-tight">{quote.ref}</h1>
            <QuoteStatusBadge status={quote.status} />
            {quote.app_originated && <Badge tone="brand">App originated</Badge>}
          </div>
          <p className="mt-1.5 text-[15px] text-muted">
            {quote.contact.company ?? quote.contact.name} &middot;{' '}
            {formatDate(quote.created_at)}
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="space-y-8">
          {/* --- Price it ------------------------------------------------- */}
          {canPrice && (
            <section className="rounded-[10px] border border-line bg-panel p-5">
              <h2 className="text-lg font-bold tracking-[-0.02em]">Confirm pricing</h2>
              {/* Constitution §14: absent is not zero. `?? 0` here said
                  "Customer saw $0.00 per unit", which is a statement of fact
                  about what somebody was shown, and it was false for every
                  quote with no estimate. The one real quote in the client's
                  database is exactly that. R-284. */}
              <p className="mt-1 text-[12.5px] text-muted">
                {quote.est_unit_price_cents === null || quote.est_unit_price_cents === undefined
                  ? 'No estimate was shown to the customer for this quote.'
                  : `Customer saw ${formatCents(quote.est_unit_price_cents)} per unit and `
                    + `${formatCents(quote.est_setup_cents ?? 0)} setup.`}
              </p>
              <form action={priceQuoteAction} className="mt-5 space-y-5">
                <input type="hidden" name="ref" value={quote.ref} />
                <div className="flex flex-wrap gap-4">
                  <Field label="Unit price (AUD)" htmlFor="unit_price">
                    <input
                      id="unit_price"
                      name="unit_price"
                      inputMode="decimal"
                      defaultValue={
                        // Empty, not "0.00". A field pre-filled with a
                        // plausible number is a field somebody accepts.
                        quote.est_unit_price_cents == null
                          ? ''
                          : (quote.est_unit_price_cents / 100).toFixed(2)
                      }
                      className={`${inputClass} w-[150px] font-mono`}
                    />
                  </Field>
                  <Field label="Setup (AUD)" htmlFor="setup">
                    <input
                      id="setup"
                      name="setup"
                      inputMode="decimal"
                      defaultValue={
                        quote.est_setup_cents == null
                          ? ''
                          : (quote.est_setup_cents / 100).toFixed(2)
                      }
                      className={`${inputClass} w-[150px] font-mono`}
                    />
                  </Field>
                </div>
                <Field
                  label="Lead time"
                  htmlFor="lead_time"
                  hint="Shown to the customer as written."
                >
                  <input
                    id="lead_time"
                    name="lead_time"
                    defaultValue="10 business days"
                    className={`${inputClass} max-w-[300px]`}
                  />
                </Field>
                <div className="flex flex-wrap items-center gap-5">
                  <Button type="submit">Send priced quote</Button>
                </div>
              </form>

              <form action={declineQuoteAction} className="mt-5 border-t border-line pt-5">
                <input type="hidden" name="ref" value={quote.ref} />
                {/* Tertiary weight, kept away from the primary action. */}
                <button
                  type="submit"
                  className="py-2 text-sm font-semibold text-muted underline underline-offset-4 hover:text-ink"
                >
                  Decline this quote
                </button>
              </form>
            </section>
          )}

          {/* --- Proof rounds --------------------------------------------- */}
          <section className="rounded-[10px] border border-line bg-panel p-5">
            <h2 className="text-lg font-bold tracking-[-0.02em]">Artwork proofs</h2>

            {quote.proofs.length === 0 ? (
              <p className="mt-2 text-[15px] text-muted">No rounds sent yet.</p>
            ) : (
              <ol className="mt-4 space-y-3">
                {quote.proofs.map((proof) => (
                  <li key={proof.id} className="rounded-[10px] border border-line p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-bold">Round {proof.round}</p>
                      <Badge
                        tone={
                          proof.status === 'approved'
                            ? 'ok'
                            : proof.status === 'change_requested'
                              ? 'warn'
                              : 'neutral'
                        }
                      >
                        {proof.status === 'approved'
                          ? 'Approved'
                          : proof.status === 'change_requested'
                            ? 'Changes requested'
                            : 'Awaiting customer'}
                      </Badge>
                    </div>
                    {proof.note && <p className="mt-2 text-[15px]">{proof.note}</p>}
                    {proof.response_note && (
                      <p className="mt-2 rounded-[8px] bg-raised p-3 text-[15px]">
                        Customer: {proof.response_note}
                      </p>
                    )}
                    <p className="mt-2 text-sm text-muted">
                      Sent {formatDate(proof.created_at)}
                      {proof.responded_at && `, answered ${formatDate(proof.responded_at)}`}
                    </p>
                  </li>
                ))}
              </ol>
            )}

            {canProof && (
              <form action={addProofAction} className="mt-5 space-y-5 border-t border-line pt-5">
                <input type="hidden" name="ref" value={quote.ref} />
                <Field
                  label="Proof image URL"
                  htmlFor="image_url"
                  // The studio does not care which company holds the file, and
                  // naming one puts another brand in front of the client's
                  // staff. It said "on R2", then "in the S3 bucket": a vendor
                  // name where a person needed an instruction. R-331, R-309.
                  hint="The customer sees this picture, so open the link and check it before you send it."
                >
                  <input
                    id="image_url"
                    name="image_url"
                    defaultValue={quote.design_url ?? ''}
                    className={inputClass}
                  />
                </Field>
                <Field label="Note to the customer" htmlFor="note" optional>
                  <textarea id="note" name="note" rows={3} className={textareaClass} />
                </Field>
                <Button type="submit">Send round {quote.proofs.length + 1}</Button>
              </form>
            )}
          </section>

          {/* --- Convert -------------------------------------------------- */}
          {canConvert && (
            <section className="rounded-[10px] border border-brand/30 bg-brand-wash-2 p-5">
              <h2 className="text-lg font-bold tracking-[-0.02em]">Convert to an order</h2>

              {/* The commission panel lived here and told Katherine what a
                  conversion would cost in commission. **This contract has no
                  commission** (FR-512), the ledger table does not exist in this
                  database, and the number it showed was fiction. T024f, R-165. */}

              <form action={convertQuoteAction} className="mt-5 space-y-5">
                <input type="hidden" name="ref" value={quote.ref} />
                <fieldset>
                  <legend className="text-sm font-semibold">Settlement</legend>
                  <div className="mt-2.5 space-y-2.5">
                    {[
                      ['stripe', 'Stripe payment link', 'Emails a payment link to the customer.'],
                      ['bank_transfer', 'Bank transfer', 'Manual. We reconcile the payment ourselves.'],
                      ['invoice', 'Invoice on terms', 'Manual. Invoice issued against agreed terms.'],
                    ].map(([value, label, hint]) => (
                      <label key={value} className="flex items-start gap-3 text-[15px]">
                        <input
                          type="radio"
                          name="settlement"
                          value={value}
                          defaultChecked={value === 'stripe'}
                          className="mt-1 size-4.5 accent-[var(--color-brand-strong)]"
                        />
                        <span>
                          <span className="font-semibold">{label}</span>
                          <span className="mt-0.5 block text-[13px] text-muted">{hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="flex flex-wrap gap-4">
                  <Field label="Deposit (AUD)" htmlFor="deposit" optional>
                    <input
                      id="deposit"
                      name="deposit"
                      inputMode="decimal"
                      defaultValue="0.00"
                      className={`${inputClass} w-[150px] font-mono`}
                    />
                  </Field>
                  <Field label="Shipping (AUD)" htmlFor="shipping" optional>
                    <input
                      id="shipping"
                      name="shipping"
                      inputMode="decimal"
                      defaultValue="0.00"
                      className={`${inputClass} w-[150px] font-mono`}
                    />
                  </Field>
                </div>

                <Button type="submit" size="lg">
                  Convert to order
                </Button>
              </form>
            </section>
          )}
        </div>

        {/* --- Sidebar ---------------------------------------------------- */}
        <aside className="space-y-6">
          <div className="rounded-[10px] border border-line bg-panel p-5">
            <h2 className="text-sm font-bold">Customer</h2>
            <dl className="mt-3 space-y-2 text-[15px]">
              <div>
                <dt className="text-muted">Name</dt>
                <dd className="font-semibold">{quote.contact.name}</dd>
              </div>
              {quote.contact.company && (
                <div>
                  <dt className="text-muted">Company</dt>
                  <dd className="font-semibold">{quote.contact.company}</dd>
                </div>
              )}
              <div>
                <dt className="text-muted">Email</dt>
                <dd className="font-semibold break-all">
                  <a href={`mailto:${quote.contact.email}`} className="hover:underline">
                    {quote.contact.email}
                  </a>
                </dd>
              </div>
              {quote.contact.phone && (
                <div>
                  <dt className="text-muted">Phone</dt>
                  <dd className="font-semibold">{quote.contact.phone}</dd>
                </div>
              )}
            </dl>
            {quote.notes && (
              <>
                <h3 className="mt-4 text-sm font-bold">Their notes</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed">{quote.notes}</p>
              </>
            )}
          </div>

          {product && (
            <div className="rounded-[10px] border border-line bg-panel p-5">
              <h2 className="text-sm font-bold">The job</h2>
              <div className="checkerboard mt-3 rounded-[10px] border border-line">
                <GarmentMockup
                  type={product.type}
                  colorHex={
                    product.colors.find((c) => c.name === quote.color)?.hex ??
                    product.colors[0]!.hex
                  }
                  designUrl={quote.design_url}
                  placement={{
                    pos_x: quote.pos_x ?? 50,
                    pos_y: quote.pos_y ?? 48,
                    scale: quote.scale ?? 60,
                  }}
                  showZone
                  label={quote.color ? `${product.name} in ${quote.color}` : product.name}
                  className="aspect-square w-full"
                />
              </div>
              <dl className="mt-4 space-y-2 text-[15px]">
                {[
                  ['Garment', product.name],
                  /**
                   * A blank row reads as data that went missing. Neither is
                   * required on a quote: a studio quote raised over the
                   * telephone often settles the colour later, and a team order
                   * is a size run rather than one size. R-275.
                   */
                  ['Colour', quote.color ?? 'Not decided'],
                  ['Size', quote.size ?? 'Mixed'],
                  ['Method', quote.method_key],
                  ['Colours', String(quote.colours)],
                  ['Locations', quote.locations.map(locationLabel).join(', ')],
                  ['Quantity', String(quote.qty)],
                  ['Channel', quote.channel],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <dt className="text-muted">{k}</dt>
                    <dd className="text-right font-semibold">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
